import { useState } from 'react'
import { Link } from 'react-router-dom'

import Disclosure from './Disclosure.jsx'
// P6-186 lifted `Person` out of this file so the music panel names a departed
// member the same way. One join site, one spelling — see Person.jsx.
import Person from './Person.jsx'
import { nameFor } from '../directory.js'
import { freshness, shortDate } from './freshness.js'
import {
  LOADING,
  MINE_FAILURE_NOTE,
  READY,
  attendanceNote,
  lastVerdictNight,
  latestChampion,
  nightsOf,
  posterUrl,
  rotationSlots,
  slotLabel,
  upcomingNight,
} from './movie.js'
import { breakdownChips, starBar, tierText, winningTier } from './vocabulary.js'

// The movie-night panel — the portal's load-bearing surface. P6-83.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 EVERY NUMBER ON THIS PANEL COMES OFF THE BLOB. THE ONES THAT DO NOT ARE
//    ABSENT, NOT ESTIMATED.
// ═══════════════════════════════════════════════════════════════════════════
//
// P6-156's standing rule, which `utils/projection_publisher.py` states as *"a
// count is a claim; an absence is not"*: where the store has no source for a
// number, the blob carries `null` and the page renders NOTHING — never `0`. The
// publisher already applies it (`attendee_count` is `null`, not `0`, for a night
// finalized before attendance tracking existed) and this file is the other end
// of the same rule.
//
// Things the design's frame draws that this page does or does not have a source
// for. They are listed rather than quietly dropped, because the next person will
// look for them — and TWO OF THEM MOVED ON 2026-09-04, WHICH IS THE POINT OF
// KEEPING THE LIST HONEST:
//
//   ✅ the whole rotation, one disc per member in schedule order — DRAWN (P6-171)
//       P6-165 publishes `shared.rotation`: an ordered `participants` list with
//       each member's 1-based slot, plus `current_picker_id`. This list used to
//       say *"3a carries no participant list"*, which was true when P6-83 wrote
//       it and false the day #406 merged. A stale comment justifying a
//       limitation is how a wrong value gets taught, so it is corrected here
//       rather than left as the reason nobody looked again.
//
//   ⏸️ the day and time ("Tonight · Thu", "20:00") — PUBLISHED, NOT RENDERED
//       ⚠️ THE REASON CHANGED, SO THE SENTENCE HAD TO. It used to be *"not in
//       the blob"*. It is now: `shared.schedule` carries `iso_weekday`, `hour`
//       and `timezone` — the guild's RECURRING setting, three values copied out
//       of the store. What is still absent is A DATE. `utils/projection_publisher.py`
//       refuses to derive one from a `week` key on purpose (the manager owns
//       that arithmetic), and the page cannot fill the gap either: turning a
//       slot number into "24 Sep" needs one night per week with no skips, which
//       is an assumption about a rotation that advances when somebody runs
//       `!movie end` and not when a calendar says so. So the discs below carry
//       SLOT NUMBERS where the handoff draws dates. A number off the blob
//       cannot be wrong; a projected date can, and nothing would log it.
//
//   ⏸️ the `NIGHTS` stat beside `PICKED` — WITHDRAWN, AND SAID IN WORDS INSTEAD
//       ⚠️ IT WAS NEVER MISSING; IT WAS MISLABELLED, WHICH IS WORSE. P6-173:
//       `attendees` is written by `finalize_movie_night` / `record_retro_night`
//       and nothing else, so a night with no `!movie start`, a night under
//       thirty minutes in voice, and every night predating attendance tracking
//       contribute zero. Jordan read his own card as `12 NIGHTS / 13 PICKED`
//       with sixty-odd nights behind him — and the anomalous number is the
//       first one. `nights_picked` is COMPLETE (every history row names a
//       picker) and `nights_attended` cannot be, so the row of two numerals at
//       one type size was the page asserting they were comparable.
//       ✅ The publisher now sends `nights_attended: null` where nothing was
//       logged and `shared.nights_with_attendance` as the denominator, and the
//       count ships as a sentence that names what it counted. `attendanceNote`
//       is the one place that writes it.
//
//   ⛔ a picker's own average ("Sam's picks average 4.2") — REFUSED UPSTREAM
//       `avg_rating_received` is published in the READER's 3b object and nowhere
//       else. Somebody else's average is somebody else's datum.
//   ⛔ the flair word and "4th of 6 pickers" — REFUSED ON A MEASUREMENT
//       P6-165 refused to publish these after measuring that this repo holds two
//       answers to *"the average the room gave this member's picks"*:
//       `compute_user_movie_stats` scores from `final_ratings` only, while
//       `avg_rating_received` falls back to `partial_ratings`. On one real
//       member that is 5.0 → Crowd-Pleaser against 2.5 → Risk-Taker, and a test
//       pins the disagreement. Either spelling ships a word contradicting the
//       number beside it — the exact failure gotcha 21 is about.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 NOTHING PERSONAL IS AVERAGED, AND THERE IS NO "RATED" STAT
// ═══════════════════════════════════════════════════════════════════════════
//
// Per-user votes are never persisted anywhere in this repo — Discord's poll
// gives the bot counts per option and nothing else — so a verdict renders as a
// TIER CHIP and never as a number belonging to a person. See vocabulary.js.

/** How many nights the disclosure lists. The handoff's number, and the reason
 * is the phone: five rows is a glance, fifty is a page. */
const RECENT = 5

/**
 * The 2:3 poster slot — the real image, or the designed absence. P6-171.
 *
 * 🔴 THE ABSENCE IS A STATE, NOT A FAILURE. `utils/projection_publisher.py`
 * publishes `poster_path` as `null` and never `""` precisely so this page can
 * tell *"we never had a poster for this"* from *"the path is blank"* — its own
 * words are *"the second is a bug and the first is 2019"*. A night added by
 * hand, or one from before the bot stored posters, gets the stripe block and a
 * label that says what it is. It is not an error and it does not apologise.
 *
 * ⚠️ A 404 LANDS IN THE SAME STATE, DELIBERATELY. TMDb paths rot, exactly as
 * `src/trips/EntryCard.jsx` records for unfurled photos. *"There is no poster
 * to show"* is one fact however it came about, and a broken-image glyph is the
 * only other option — so `onError` falls back to the stripe rather than
 * inventing a second sentence about somebody else's CDN.
 */
function PosterSlot({ night }) {
  const [broken, setBroken] = useState(false)
  const src = broken ? null : posterUrl(night?.poster_path)
  return (
    <span className="d2d-portal__poster" data-poster={src ? 'true' : 'false'}>
      {src ? (
        <img
          className="d2d-portal__posterimg"
          src={src}
          // 🔴 EMPTY ALT. The film's title is the very next thing in the
          // document and is real text; a screen reader announcing
          // "image, The Assassination of Jesse James…" would say the same
          // sentence twice. Decoration beside its own caption.
          alt=""
          loading="lazy"
          decoding="async"
          // TMDb does not need to know which page is looking at a poster, and
          // this is the one-word way to not tell it. Same choice EntryCard
          // already made for the trips board.
          referrerPolicy="no-referrer"
          onError={() => setBroken(true)}
          data-testid="lead-poster"
        />
      ) : (
        <span className="d2d-micro" data-testid="lead-poster-absent">
          no poster
        </span>
      )}
    </span>
  )
}

/** A night's date, from a real timestamp or not at all. See freshness.js on why
 * a `week` key is never turned into one here. */
function nightDate(night) {
  return shortDate(night?.finalized_at) ?? shortDate(night?.ratings_finalized_at)
}

/**
 * One disc per member of the rotation, in schedule order. P6-171.
 *
 * 🔴 COLOUR IS NEVER THE ONLY CARRIER, and both halves of that rule are load
 * bearing here. "Who is up" is the sunset gradient AND the word `Now`; "you" is
 * the ember ring AND the word `You`. A member who cannot tell the two oranges
 * apart reads the same two facts off the labels.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 TWO PUBLISHED ANSWERS TO "WHO IS UP", AND THE STRIP FOLLOWS THE ROTATION
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The blob carries `rotation.current_picker_id` AND `nights[0].picker_id` on an
 * unfinalized night, and they answer different questions:
 *
 *   `rotation.current_picker_id`  whose TURN it is. Mirrors
 *                                 `MovieNightManager.get_current_picker`, and
 *                                 `test_the_published_current_picker_is_the_managers`
 *                                 drives the real manager through join →
 *                                 generate → advance → complete asserting the
 *                                 two agree at every step.
 *   the newest unfinalized night  who made the pick that has not played yet.
 *
 * ⚠️ THEY AGREE IN THE ORDINARY MID-WEEK STATE AND NOTHING TIES THEM TOGETHER.
 * `record_movie_pick` appends the history row and the per-user stats; the
 * rotation is credited by a SEPARATE call from the cog. Three measured ways
 * they come apart:
 *
 *   · a pick made outside the rotation flow (admin `setwinner`, a swap, a
 *     forfeit replacement) leaves `current_index` where it was, so the newest
 *     pick's holder is not the member whose turn it is;
 *   · `record_retro_night` writes a FINALIZED row for a volunteer and inserts
 *     them at `current_index` so *"the postponer stays current"* — there is
 *     then no unfinalized night at all, and only the rotation knows who is up;
 *   · a rotation can be `completed` — which the publisher refuses to name a
 *     picker for — while a picked film is still unwatched.
 *
 * THIS STRIP IS ABOUT WHOSE TURN IT IS, so it reads the rotation. The lead block
 * above is about the film, so it keeps reading the night. Neither derives the
 * other's answer, which is gotcha 21 avoided by not creating the seam.
 *
 * ⚠️ THE FALLBACK IS NOT DEAD CODE AND MUST NOT BE DELETED. `rotation` is a
 * P6-165 field, and the projection sitting in the bucket today was written by
 * P6-155 without it. With no participant list this renders exactly what P6-83
 * shipped — the newest pick's holder, and the reader's own slot out of their 3b
 * object — which is still the honest answer for that blob rather than an empty
 * strip under a heading promising a rotation.
 *
 * 🔴 THE DASHED `+ You?` IS THE NEW MEMBER'S INVITATION AND IS NOT DROPPED. It
 * is the one control-shaped thing on a screen that otherwise tells somebody with
 * no history that they have no history, and the handoff is explicit that the
 * empty state is what sells or kills this page.
 */
function RotationStrip({ directory, shared, upcoming, mine, mineKnown }) {
  const slots = rotationSlots(shared)
  const you = typeof mine?.user_id === 'string' ? mine.user_id : null
  const inRotation = mine?.in_rotation === true
  const position = Number.isInteger(mine?.rotation_position) ? mine.rotation_position : null

  // Whose turn it is. From the rotation when there is one — see the header on
  // why the night's picker is a different fact and only the fallback for it.
  const published = shared?.rotation?.current_picker_id
  const nowPicker =
    slots.length > 0
      ? typeof published === 'string'
        ? published
        : null
      : typeof upcoming?.picker_id === 'string'
        ? upcoming.picker_id
        : null
  const youAreUp = nowPicker !== null && you !== null && you === nowPicker

  // 🔴 `slot N`, NOT AN ORDINAL — P6-177. This read `you are 4th` while
  // PortalHome's subline said `In the rotation · slot 4` about the same member
  // on the same screen. `slotLabel` is the one spelling; the discs' own
  // screen-reader copy below uses it too, so a fourth wording cannot appear
  // without deleting the function.
  const label = !mineKnown
    ? 'Rotation'
    : inRotation
      ? position === null
        ? 'Rotation · you are in it'
        : `Rotation · you are in ${slotLabel(position)}`
      : 'Rotation · joining opens when this one runs out'

  return (
    <div className="d2d-portal__rotation" data-testid="rotation-strip">
      <span className="d2d-portal__rotationlabel d2d-micro">{label}</span>
      <ol className="d2d-portal__discs">
        {slots.length > 0
          ? slots.map((slot) => (
              <RotationDisc
                key={slot.user_id + ':' + slot.position}
                directory={directory}
                userId={slot.user_id}
                position={slot.position}
                now={slot.user_id === nowPicker}
                you={slot.user_id === you}
              />
            ))
          : // The pre-P6-165 blob. Two discs at most, and each one is a fact
            // that blob genuinely carries.
            [
              nowPicker ? (
                <RotationDisc
                  key="fallback-now"
                  directory={directory}
                  userId={nowPicker}
                  position={null}
                  now
                  you={youAreUp}
                />
              ) : null,
              inRotation && !youAreUp ? (
                <RotationDisc
                  key="fallback-you"
                  directory={directory}
                  userId={you}
                  position={position}
                  now={false}
                  you
                />
              ) : null,
            ]}
        {mineKnown && !inRotation ? (
          <li>
            <span className="d2d-portal__disc" data-invite="true" aria-hidden="true">
              +
            </span>
            <span className="d2d-portal__disclabel d2d-micro" data-testid="rotation-invite">
              You?
            </span>
          </li>
        ) : null}
      </ol>
    </div>
  )
}

/**
 * One member's slot.
 *
 * ⚠️ THE VISIBLE NAME IS `aria-hidden` AND THE SLOT CARRIES ITS OWN SENTENCE,
 * so a screen reader gets "Slot 3: Sam" once rather than the name twice with a
 * bare numeral between. `Person` is used for the spoken copy because it is the
 * one join site — a member who has left is announced as such, and an id the
 * directory cannot name becomes the neutral placeholder instead of a raw id.
 *
 * ⚠️ NO NAME IS DRAWN WHEN THE DIRECTORY COULD NOT ANSWER. Twelve discs each
 * captioned "someone in the crew" is noise pretending to be a schedule; the
 * numbered discs and the directory's own outage note are the honest state.
 */
function RotationDisc({ directory, userId, position, now, you }) {
  const person = nameFor(directory, typeof userId === 'string' ? userId : null)
  const state = now ? (you ? 'Now · You' : 'Now') : you ? 'You' : null
  return (
    <li>
      <span
        className="d2d-portal__disc"
        data-now={now ? 'true' : 'false'}
        data-you={you ? 'true' : 'false'}
        aria-hidden="true"
      >
        {position ?? ''}
      </span>
      {person.known ? (
        <span className="d2d-portal__discname" aria-hidden="true">
          {person.label}
        </span>
      ) : null}
      {state ? (
        <span
          className="d2d-portal__disclabel d2d-micro"
          data-testid={now ? 'rotation-now' : 'rotation-you'}
        >
          {state}
        </span>
      ) : null}
      <span className="d2d-sr-only">
        {now ? 'Up now: ' : position === null ? '' : `${slotLabel(position, { capitalized: true })}: `}
        <Person directory={directory} userId={userId} />
      </span>
    </li>
  )
}

/** The room's verdict on the last night that has one. Tier chips, never a
 * number attributed to anybody. */
function VerdictBlock({ directory, night, scaleMax }) {
  if (night === null) {
    return (
      <div className="d2d-portal__block">
        <span className="d2d-portal__blocklabel d2d-micro">Last night · the room’s verdict</span>
        <p className="d2d-portal__flat">No night has been rated yet.</p>
      </div>
    )
  }
  const chips = breakdownChips(night.rating?.breakdown)
  const top = winningTier(night.rating?.breakdown)
  const votes = Number(night.rating?.votes)
  const average = night.rating?.average
  const date = nightDate(night)
  // The meta line is assembled from the parts that exist, so a night missing one
  // of them reads as a shorter sentence rather than as "undefined · 6 votes".
  const meta = [
    date,
    Number.isFinite(votes) && votes > 0 ? `${votes} vote${votes === 1 ? '' : 's'}` : null,
    average === null || average === undefined || !Number.isFinite(Number(average))
      ? null
      : `${Number(average).toFixed(1)} out of ${scaleMax}`,
  ].filter(Boolean)

  return (
    <div className="d2d-portal__block" data-testid="verdict-block">
      <span className="d2d-portal__blocklabel d2d-micro">Last night · the room’s verdict</span>
      <span className="d2d-portal__verdict">
        {top ? (
          <span className="d2d-portal__toptier" data-testid="top-tier">
            {tierText(top.label)}
          </span>
        ) : null}
        <span className="d2d-portal__verdicttitle">{night.title ?? 'Untitled'}</span>
      </span>
      {chips.length > 0 ? (
        <ul className="d2d-portal__chips" data-testid="verdict-chips">
          {chips.map((chip) => (
            <li key={chip.label} data-known={chip.known ? 'true' : 'false'}>
              {chip.text}
            </li>
          ))}
        </ul>
      ) : (
        // A night added by hand carries a real average and an EMPTY breakdown —
        // measured, two paths in cogs/movie/cog.py. Saying so beats a row of
        // nothing that reads as a rendering bug.
        <p className="d2d-portal__flat">No per-tier breakdown was kept for this one.</p>
      )}
      {meta.length > 0 ? <p className="d2d-portal__meta">{meta.join(' · ')}</p> : null}
      <p className="d2d-portal__meta">
        Picked by <Person directory={directory} userId={night.picker_id} />.
      </p>
    </div>
  )
}

/**
 * Your own record. PICKED, the room's average of your picks, and attendance in
 * words rather than as a number pretending to be the same kind of thing.
 *
 * 🔴 `NIGHTS` USED TO SIT HERE AT DISPLAY SIZE NEXT TO `PICKED` AND IT WAS THE
 * WHOLE OF P6-173. Two numerals, one type size, one row — every visual signal
 * on the screen said "these are comparable" and they are not. `nights_picked`
 * is complete: every history row names a picker, so the count is the answer.
 * `nights_attended` is a count over the nights somebody happened to log, which
 * on this server is a small minority of them, so as a bare numeral it is not an
 * understatement of the truth — it is a different question wearing the label of
 * this one.
 *
 * ✅ The count still ships, because it is real and it is theirs. It ships as a
 * sentence, where it can carry the denominator that makes it readable, and
 * `attendanceNote` is the one place that writes it.
 */
function RecordBlock({ mine, shared, scaleMax }) {
  const bar = starBar(mine.avg_rating_received, scaleMax)
  const attendance = attendanceNote(mine, shared)
  return (
    <div className="d2d-portal__block" data-testid="record-block">
      <span className="d2d-portal__blocklabel d2d-micro">Your record</span>
      <span className="d2d-portal__stats">
        <span className="d2d-portal__stat">
          {mine.nights_picked}
          <span className="d2d-portal__statlabel d2d-micro">PICKED</span>
        </span>
      </span>
      {/* 🔴 THERE IS NO "RATED" STAT AND THERE CANNOT BE ONE — per-user votes are
          never stored, so nothing anywhere knows how many nights you rated. */}
      {bar === null ? (
        <p className="d2d-portal__flat" data-testid="no-average">
          The room has not rated one of your picks yet.
        </p>
      ) : (
        <span className="d2d-portal__stars">
          <span aria-hidden="true" className="d2d-portal__starfill">
            {bar.fill}
            <span className="d2d-portal__starempty">{bar.empty}</span>
          </span>
          <span className="d2d-portal__starlabel d2d-micro" data-testid="your-average">
            {bar.text} · your picks
          </span>
        </span>
      )}
      {/* `null` when the blob cannot support an honest sentence — a projection
          published before P6-173 has the count and not its denominator. Nothing
          is rendered rather than the old number: see `attendanceNote`. */}
      {attendance === null ? null : (
        <p className="d2d-portal__flat" data-testid="attendance-note">
          {attendance}
        </p>
      )}
    </div>
  )
}

/** Movie of the Year. The newest crowned season, and whether it was yours. */
function MotyBlock({ directory, champion, mine }) {
  if (champion === null) {
    return (
      <div className="d2d-portal__block">
        <span className="d2d-portal__blocklabel d2d-micro">Movie of the Year</span>
        <p className="d2d-portal__flat">No season has been settled yet.</p>
      </div>
    )
  }
  const wins = Array.isArray(mine?.moty_wins) ? mine.moty_wins : []
  const yours = wins.some((win) => win?.season_id === champion.season_id)
  return (
    <div className="d2d-portal__block" data-testid="moty-block">
      <span className="d2d-portal__blocklabel d2d-micro">Movie of the Year</span>
      <span className="d2d-portal__verdict">
        <span className="d2d-portal__motyyear">{champion.label}</span>
        <span className="d2d-portal__verdicttitle">{champion.title ?? 'Untitled'}</span>
      </span>
      <p className="d2d-portal__meta">
        {yours ? (
          <>Winner · your pick.</>
        ) : (
          <>
            Picked by <Person directory={directory} userId={champion.picker_id} />.
          </>
        )}
        {wins.length > 0 ? ` You have ${wins.length} of these.` : null}
      </p>
    </div>
  )
}

/**
 * The new member's two blocks.
 *
 * 🔴 NO ILLUSTRATION, NO APOLOGY — the handoff's empty-state rule, and Slot.jsx
 * already carries the site's version of it: one flat sentence, at most one dry
 * follow-up. Both of these are statements about how the product works, which is
 * the only kind of sentence available to a page with no data about this person.
 */
function NewMemberBlocks() {
  return (
    <div className="d2d-portal__blocks" data-testid="new-member-blocks">
      <div className="d2d-portal__block">
        <span className="d2d-portal__blocklabel d2d-micro">How rating works</span>
        <p className="d2d-portal__flat">
          A poll opens near the end of the film — six buttons, Godtier down to Nahhh. Only the
          totals are kept, never who pressed what.
        </p>
      </div>
      <div className="d2d-portal__block">
        <span className="d2d-portal__blocklabel d2d-micro">Your turn to pick</span>
        <p className="d2d-portal__flat">
          The next rotation opens when this one runs out. The bot posts a Join button — press it
          and you get a week.
        </p>
      </div>
    </div>
  )
}

export default function MoviePanel({ movie, directory, now, docPath }) {
  const [nightsOpen, setNightsOpen] = useState(false)

  const stamp = freshness(movie.state === READY ? movie.shared?.as_of : null, now)

  return (
    <section className="d2d-portal__movie" aria-labelledby="portal-movie" data-testid="movie-panel">
      <span className="d2d-portal__rule" aria-hidden="true" />
      <span className="d2d-portal__wash" aria-hidden="true" />
      <div className="d2d-portal__moviebody">
        <div className="d2d-portal__panelhead">
          {/* The heading shortens before the stamp does — the handoff's
              instruction after two measured 375px overflows, one of which was
              exactly this row with two nowrap children. */}
          <h2 id="portal-movie" className="d2d-portal__paneltitle d2d-micro">
            Movie night
          </h2>
          {movie.state === LOADING ? null : (
            <span
              className="d2d-portal__stamp d2d-micro"
              title={stamp.title ?? undefined}
              data-testid="movie-as-of"
            >
              {movie.state === READY ? stamp.label : 'Unavailable'}
            </span>
          )}
        </div>

        {movie.state === LOADING ? (
          // Nothing has failed yet, so nothing is claimed. src/directory.js's
          // three-state rule, on the panel that would shout loudest if it got
          // this wrong.
          <p className="d2d-portal__flat" data-testid="movie-loading">
            Loading your movie record…
          </p>
        ) : movie.state !== READY ? (
          <p className="d2d-portal__flat" data-testid="movie-unavailable">
            Movie night is unavailable right now. Everything else on this page is unaffected.
          </p>
        ) : (
          <MovieBody
            movie={movie}
            directory={directory}
            nightsOpen={nightsOpen}
            onToggleNights={() => setNightsOpen((was) => !was)}
          />
        )}

        {/* 🔴 P6-316. ONE LINK, READ FROM `docPath`, AT THE FOOT — AS `MusicPanel`
            DRAWS ITS OWN. It used to be typed as `to="/docs/movie-night"` and drawn
            only for a member with no record, so nothing could notice it pointing at
            a page that had moved, and everybody else had no link to it from here.
            The design (HANDOFF-portal-home.md §3, frame 3a) puts *How movie night
            works →* in the new-member variant, where it is the LAST element of the
            panel; the member with a record has no link in the design and gets one
            here. ⚠️ Moving it to the foot moves it for a new member too, one disclosure
            row down — this site shows them the room's nights, which frame 3a does
            not. Drawn in every state: a docs page does not depend on `/api/movie`. */}
        {docPath ? (
          <Link className="d2d-portal__more d2d-micro" to={docPath}>
            How movie night works →
          </Link>
        ) : null}
      </div>
    </section>
  )
}

function MovieBody({ movie, directory, nightsOpen, onToggleNights }) {
  const shared = movie.shared
  const mine = movie.mine
  const scaleMax = Number.isFinite(Number(shared?.scale_max)) ? Number(shared.scale_max) : 5
  const nights = nightsOf(shared)
  const upcoming = upcomingNight(shared)
  const verdictNight = lastVerdictNight(shared)
  const champion = latestChampion(shared)
  const lead = upcoming ?? nights[0] ?? null
  const recorded = Number.isFinite(Number(shared?.nights_recorded))
    ? Number(shared.nights_recorded)
    : nights.length

  // 🔴 "YOUR FIRST ONE" IS A CLAIM ABOUT SOMEBODY'S HISTORY AND IT NEEDS
  // EVIDENCE. It used to render whenever `nights_attended` was absent or zero,
  // which is three different situations wearing one face:
  //
  //   mine === null, mineFailure === null   genuinely new — the designed state
  //   mine === null, mineFailure !== null   WE COULD NOT READ THEIR RECORD, and
  //                                         the panel greeted them as a newcomer
  //                                         directly above the note saying so
  //   nights_attended absent/0              nobody LOGGED a night they were at
  //
  // The second is the failure this file already refuses one block down ("NOT
  // THE NEW-MEMBER STATE... it would be invisible because that state renders
  // perfectly") and the lead block was doing it anyway. The third is P6-173.
  //
  // ✅ So the invitation now needs the store to have LOOKED: either it holds
  // nothing about this member at all, or it holds two measured zeros.
  const brandNew =
    movie.mineFailure === null &&
    (mine === null || (mine.nights_picked === 0 && mine.nights_attended === 0))

  return (
    <>
      {lead === null ? (
        <p className="d2d-portal__flat" data-testid="no-nights">
          No movie night has been recorded here yet.
        </p>
      ) : (
        <div className="d2d-portal__lead" data-testid="lead-block">
          <PosterSlot night={lead} />
          <div className="d2d-portal__leadtext">
            <span className="d2d-portal__leadlabel d2d-micro">
              {/* Two different facts, and the store tells them apart: a history
                  row exists from the moment a pick is made, so an unfinalized
                  newest row is this week's film and not last week's. */}
              {upcoming ? 'Picked for this week' : 'The last one'}
            </span>
            <span className="d2d-portal__leadtitle">{lead.title ?? 'Untitled'}</span>
            <span className="d2d-portal__meta">
              Picked by <Person directory={directory} userId={lead.picker_id} />
              {upcoming ? null : nightDate(lead) ? ` · ${nightDate(lead)}` : null}
            </span>
            {upcoming ? (
              <span className="d2d-portal__meta">
                The rating poll opens near the end of the film.
              </span>
            ) : null}
            {/* ⚠️ THE COUNT IS NOT REPEATED HERE. This line used to read
                `You have been to 61 of these.` — the numeral P6-173 is about,
                in a second place, with "these" standing in for a denominator
                that was never its own. It is said once now, in the record
                block, with the denominator that makes it readable. */}
            {brandNew ? (
              <span className="d2d-portal__you" data-testid="personal-line">
                Your first one. Nothing to type — turn up in the voice channel.
              </span>
            ) : null}
          </div>
        </div>
      )}

      <RotationStrip
        directory={directory}
        shared={shared}
        upcoming={upcoming}
        mine={mine}
        mineKnown={movie.mineFailure === null}
      />

      {movie.mineFailure !== null ? (
        // 🔴 NOT THE NEW-MEMBER STATE. Showing "you are new here" to somebody
        // with six years of nights is the portal asserting something false about
        // their own history, and it would be invisible because that state
        // renders perfectly.
        <p className="d2d-portal__flat" data-testid="mine-failure">
          {MINE_FAILURE_NOTE[movie.mineFailure]}
        </p>
      ) : mine === null ? (
        <NewMemberBlocks />
      ) : (
        <div className="d2d-portal__blocks">
          <VerdictBlock directory={directory} night={verdictNight} scaleMax={scaleMax} />
          <RecordBlock mine={mine} shared={shared} scaleMax={scaleMax} />
          <MotyBlock directory={directory} champion={champion} mine={mine} />
        </div>
      )}

      {nights.length > 0 ? (
        <div className="d2d-portal__nights">
          <Disclosure
            open={nightsOpen}
            onToggle={onToggleNights}
            className="d2d-portal__nightstoggle"
            data-testid="nights-toggle"
          >
            <span>
              {nightsOpen
                ? 'Recent nights'
                : `Last ${Math.min(RECENT, nights.length)} of ${recorded} nights`}
            </span>
          </Disclosure>
          {nightsOpen ? (
            <>
              <ul className="d2d-portal__nightlist" data-testid="night-list">
                {nights.slice(0, RECENT).map((night) => {
                  const top = winningTier(night.rating?.breakdown)
                  return (
                    <li key={`${night.week}-${night.movie_id ?? night.title ?? ''}`}>
                      <span className="d2d-portal__nightdate d2d-micro">
                        {nightDate(night) ?? '—'}
                      </span>
                      <span className="d2d-portal__nightmain">
                        <span className="d2d-portal__nighttitle">{night.title ?? 'Untitled'}</span>
                        <span className="d2d-portal__nightpicker d2d-micro">
                          Picked by <Person directory={directory} userId={night.picker_id} />
                        </span>
                      </span>
                      <span className="d2d-portal__nighttier d2d-micro">
                        {top ? tierText(top.label) : 'Not rated'}
                      </span>
                    </li>
                  )
                })}
              </ul>
              {/* No "Full history →". There is no history page on this site, and
                  a link that goes nowhere is worse than none — the same rule
                  Landing.jsx already follows for a card that is not a door. */}
              <p className="d2d-portal__flat">Older nights are in the server.</p>
            </>
          ) : null}
        </div>
      ) : null}
    </>
  )
}
